import React from "react";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import useUndoneNotification from "../../components/useUndoneNotification";
import { useInputPopup } from "../../components/InputPopup";
import { Trans } from "@coworker/locales";
import Time from "../../assets/svg/time.svg?react";
import { dropTypeKeys, dropTypeOptions } from "@coworker/enums/dropTypes";
import Issue from "../../assets/svg/issue.svg?react";
import IssueOther from "@coworker/reusable/svg/three-dots.svg?react";
import taskStateOptions from "@coworker/enums/taskState";
import activityTypes from "@coworker/enums/activityTypes";
import trackerHelper from "../../helpers/tracker";
import { useUID, useTeamId } from "../../core/auth/useLoggedInUser";
import { useUpdateTaskMutation } from "@coworker/apprestructured/src/tasks/hooks/useUpdateTaskById";
import { useQueryClient } from "@tanstack/react-query";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import {
  IN_PROGRESS_TASKS_QUERY_KEY,
  OPEN_TASKS_QUERY_KEY,
  OVERVIEW_TASKS_QUERY_KEY,
} from "@coworker/apprestructured/src/TasksOverview/constants/queryKeys";

export const useUndoDropTask = ({ task, afterUndo }) => {
  const undoCurtain = useUndoneNotification();
  const uid = useUID();

  const undoTask = React.useCallback(
    async (actionId) => {
      const data = {
        last_editor_id: uid,
        state: task.state,
        assigned_user_id: task.assigned_user_id,
        drop_type: "",
        drop_description: "",
        undo_action_id: actionId || "",
      };

      await undoCurtain(data, task.id);
      afterUndo();
    },
    [afterUndo, task, uid, undoCurtain]
  );

  return {
    call: undoTask,
  };
};

export const useDropTask = (task, taskId, config = {}) => {
  const { undo, afterUndo = () => {} } = config;
  const uid = useUID();
  const teamId = useTeamId();
  const { showToastWithUndo } = useToastNotification();
  const { getInput } = useInputPopup();
  const { call: undoTask } = useUndoDropTask({
    task: { ...task, id: taskId },
    afterUndo,
  });
  const { toggleModal, modalState } = React.useContext(ModalContext);
  const updateTask = useUpdateTaskMutation();
  const queryClient = useQueryClient();
  const dropTask = React.useCallback(async () => {
    const actionId = `release-${new Date().getTime()}`;

    const isAssignedToMe = task.assigned_user_id === uid;

    const data = await getInput("taskAction", {
      customTitle: <Trans>whyAreYouDroppingString</Trans>,
      content: [
        {
          icon: Time,
          name: <Trans>{dropTypeKeys.END_OF_SHIFT}</Trans>,
          type: dropTypeOptions.END_OF_SHIFT,
        },
        {
          icon: Issue,
          name: <Trans>{dropTypeKeys.ISSUE}</Trans>,
          type: dropTypeOptions.ISSUE,
        },
        {
          icon: IssueOther,
          name: <Trans>{dropTypeKeys.OTHER}</Trans>,
          type: dropTypeOptions.OTHER,
          descriptionRequired: true,
        },
      ],
    });

    if (!data) return;

    const updateData = {
      assigned_team_id: task.assigned_team_id ?? teamId,
      last_editor_id: uid,
      state: taskStateOptions.UNASSIGNED,
      assigned_user_id: "",
      drop_type: data.issue,
      drop_description: data.description,
      action_id: actionId,
      // The onTaskUpdate trigger also deletes undo_action_id, but in the case that this backend change has not propagated to the frontend we must make sure to also signal this from the frontend!
      undo_action_id: "",
      activity_type: isAssignedToMe
        ? activityTypes.DROP
        : activityTypes.RELEASE,
    };

    await updateTask.mutateAsync({ data: updateData, taskId });

    if (modalState.isTaskDetailSheetOpen)
      toggleModal({ type: ModalType.TaskDetail });

    trackerHelper.trackDropTask(task, updateData.drop_type);

    if (!undo) return;

    showToastWithUndo(
      isAssignedToMe ? (
        <Trans>youDroppedString</Trans>
      ) : (
        <Trans>youReleasedString</Trans>
      ),
      () => undoTask(actionId)
    );

    await queryClient.invalidateQueries({
      queryKey: [OVERVIEW_TASKS_QUERY_KEY, OPEN_TASKS_QUERY_KEY],
    });
    await queryClient.invalidateQueries({
      queryKey: [OVERVIEW_TASKS_QUERY_KEY, IN_PROGRESS_TASKS_QUERY_KEY],
    });

    return true;
  }, [
    task,
    uid,
    getInput,
    teamId,
    updateTask,
    taskId,
    queryClient,
    undo,
    showToastWithUndo,
    undoTask,
    modalState.isTaskDetailSheetOpen,
    toggleModal,
  ]);

  return {
    call: dropTask,
  };
};
